import type { StorageLike } from './const';
import {
  INITIAL_GENERATION_NOTIFICATION_ARMED_TASK_TTL_MS,
  INITIAL_GENERATION_NOTIFICATION_MAX_ARMED_PROJECTS,
  INITIAL_GENERATION_NOTIFICATION_MAX_HANDLED_PROJECTS,
  INITIAL_GENERATION_NOTIFICATION_TASKS_KEY,
  SYSTEM_NOTIFICATION_PREFERENCE_CHANGED_EVENT,
  SYSTEM_NOTIFICATION_PREFERENCES_KEY,
} from './const';

interface SystemNotificationPreferenceRecord {
  permissionPrompted?: boolean;
  enabled?: boolean;
}

interface SystemNotificationPreferencesStorage {
  version: 2;
  users: Record<string, SystemNotificationPreferenceRecord>;
}

interface InitialGenerationNotificationTask {
  status: 'armed' | 'handled';
  updatedAt: number;
}

interface InitialGenerationNotificationUserTasks {
  projects: Record<string, InitialGenerationNotificationTask>;
}

interface InitialGenerationNotificationTasksStorage {
  version: 2;
  users: Record<string, InitialGenerationNotificationUserTasks>;
}

const storageWriteFallbacks = new WeakMap<StorageLike, Map<string, string>>();
const unavailableStorageFallback = new Map<string, string>();

function getStorageWriteFallback(storage: StorageLike | null) {
  if (!storage) {
    return unavailableStorageFallback;
  }

  const existing = storageWriteFallbacks.get(storage);
  if (existing) {
    return existing;
  }

  const fallback = new Map<string, string>();
  storageWriteFallbacks.set(storage, fallback);
  return fallback;
}

function getWindowLocalStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function getStorage(storage?: StorageLike | null): StorageLike | null {
  return storage === undefined ? getWindowLocalStorage() : storage;
}

function safeGetItem(storage: StorageLike | null, key: string): string | null {
  const fallbackValue = getStorageWriteFallback(storage).get(key);
  if (fallbackValue !== undefined) {
    return fallbackValue;
  }

  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSetItem(storage: StorageLike | null, key: string, value: string): void {
  if (!storage) {
    getStorageWriteFallback(storage).set(key, value);
    return;
  }

  try {
    storage.setItem(key, value);
    getStorageWriteFallback(storage).delete(key);
  } catch {
    getStorageWriteFallback(storage).set(key, value);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function readJson(storage: StorageLike | null, key: string): unknown {
  const raw = safeGetItem(storage, key);
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function readPreferencesStorage(storage: StorageLike | null): SystemNotificationPreferencesStorage {
  const parsed = readJson(storage, SYSTEM_NOTIFICATION_PREFERENCES_KEY);
  if (!isRecord(parsed) || parsed.version !== 2 || !isRecord(parsed.users)) {
    return { version: 2, users: {} };
  }

  const users = Object.fromEntries(
    Object.entries(parsed.users).flatMap(([userId, value]) => {
      if (!userId || !isRecord(value)) {
        return [];
      }

      const record: SystemNotificationPreferenceRecord = {};
      if (typeof value.permissionPrompted === 'boolean') {
        record.permissionPrompted = value.permissionPrompted;
      }
      if (typeof value.enabled === 'boolean') {
        record.enabled = value.enabled;
      }
      return [[userId, record]];
    }),
  );

  return { version: 2, users };
}

function getPreferenceRecord(userId: string, storage?: StorageLike | null) {
  return readPreferencesStorage(getStorage(storage)).users[userId];
}

function updatePreferenceRecord(
  userId: string,
  updater: (current: SystemNotificationPreferenceRecord) => SystemNotificationPreferenceRecord,
  storage?: StorageLike | null,
) {
  if (!userId) {
    return;
  }

  const targetStorage = getStorage(storage);
  const preferences = readPreferencesStorage(targetStorage);
  preferences.users[userId] = updater(preferences.users[userId] ?? {});
  safeSetItem(targetStorage, SYSTEM_NOTIFICATION_PREFERENCES_KEY, JSON.stringify(preferences));
}

export function hasPromptedSystemNotificationPermission(userId: string, storage?: StorageLike | null): boolean {
  return getPreferenceRecord(userId, storage)?.permissionPrompted === true;
}

export function markSystemNotificationPermissionPrompted(userId: string, storage?: StorageLike | null) {
  updatePreferenceRecord(userId, current => ({ ...current, permissionPrompted: true }), storage);
}

export function getSystemNotificationPreference(userId: string, storage?: StorageLike | null): boolean | undefined {
  return userId ? getPreferenceRecord(userId, storage)?.enabled : undefined;
}

function getBrowserNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
    return 'unsupported';
  }
  return window.Notification.permission;
}

export function isSystemNotificationPreferenceEnabled(
  userId: string | undefined,
  notificationPermission: NotificationPermission | 'unsupported' = getBrowserNotificationPermission(),
  storage?: StorageLike | null,
): boolean {
  return Boolean(
    userId
    && notificationPermission === 'granted'
    && getSystemNotificationPreference(userId, storage) !== false,
  );
}

export function setSystemNotificationPreference(userId: string, enabled: boolean, storage?: StorageLike | null) {
  updatePreferenceRecord(userId, current => ({ ...current, enabled }), storage);
  if (!enabled) {
    clearInitialGenerationNotificationTasks(userId, storage);
  }

  if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SYSTEM_NOTIFICATION_PREFERENCE_CHANGED_EVENT, {
      detail: { userId, enabled },
    }));
  }
}

function readTasksStorage(storage: StorageLike | null): InitialGenerationNotificationTasksStorage {
  const parsed = readJson(storage, INITIAL_GENERATION_NOTIFICATION_TASKS_KEY);
  if (!isRecord(parsed) || parsed.version !== 2 || !isRecord(parsed.users)) {
    return { version: 2, users: {} };
  }

  const users: Record<string, InitialGenerationNotificationUserTasks> = {};
  Object.entries(parsed.users).forEach(([userId, value]) => {
    if (!userId || !isRecord(value) || !isRecord(value.projects)) {
      return;
    }

    const projects: Record<string, InitialGenerationNotificationTask> = {};
    Object.entries(value.projects).forEach(([projectId, task]) => {
      if (
        projectId
        && isRecord(task)
        && (task.status === 'armed' || task.status === 'handled')
        && typeof task.updatedAt === 'number'
      ) {
        projects[projectId] = { status: task.status, updatedAt: task.updatedAt };
      }
    });
    users[userId] = { projects };
  });

  return { version: 2, users };
}

function pruneUserTasks(userTasks: InitialGenerationNotificationUserTasks, now = Date.now()) {
  const tasks = Object.entries(userTasks.projects).filter(([, task]) => (
    task.status === 'handled' || now - task.updatedAt <= INITIAL_GENERATION_NOTIFICATION_ARMED_TASK_TTL_MS
  ));
  const armed = tasks
    .filter(([, task]) => task.status === 'armed')
    .sort(([, left], [, right]) => right.updatedAt - left.updatedAt)
    .slice(0, INITIAL_GENERATION_NOTIFICATION_MAX_ARMED_PROJECTS);
  const handled = tasks
    .filter(([, task]) => task.status === 'handled')
    .sort(([, left], [, right]) => right.updatedAt - left.updatedAt)
    .slice(0, INITIAL_GENERATION_NOTIFICATION_MAX_HANDLED_PROJECTS);

  userTasks.projects = Object.fromEntries([...armed, ...handled]);
  return userTasks;
}

function writeTasksStorage(storage: StorageLike | null, value: InitialGenerationNotificationTasksStorage) {
  Object.entries(value.users).forEach(([userId, userTasks]) => {
    pruneUserTasks(userTasks);
    if (!Object.keys(userTasks.projects).length) {
      delete value.users[userId];
    }
  });
  safeSetItem(storage, INITIAL_GENERATION_NOTIFICATION_TASKS_KEY, JSON.stringify(value));
}

function getUserTasks(userId: string, storage?: StorageLike | null) {
  const targetStorage = getStorage(storage);
  const tasks = readTasksStorage(targetStorage);
  const current = tasks.users[userId] ?? { projects: {} };
  const previousCount = Object.keys(current.projects).length;
  const pruned = pruneUserTasks(current);
  if (tasks.users[userId] && previousCount !== Object.keys(pruned.projects).length) {
    tasks.users[userId] = pruned;
    writeTasksStorage(targetStorage, tasks);
  }
  return pruned;
}

function updateUserTasks(
  userId: string,
  updater: (current: InitialGenerationNotificationUserTasks) => InitialGenerationNotificationUserTasks,
  storage?: StorageLike | null,
) {
  if (!userId) {
    return;
  }

  const targetStorage = getStorage(storage);
  const tasks = readTasksStorage(targetStorage);
  tasks.users[userId] = updater(pruneUserTasks(tasks.users[userId] ?? { projects: {} }));
  writeTasksStorage(targetStorage, tasks);
}

function getNextTaskUpdatedAt(userTasks: InitialGenerationNotificationUserTasks) {
  const latest = Math.max(0, ...Object.values(userTasks.projects).map(task => task.updatedAt));
  return Math.max(Date.now(), latest + 1);
}

export function getInitialGenerationNotificationOwnerId(projectId: string, userId?: string | null) {
  return userId || `anonymous-project:${projectId}`;
}

export function claimAnonymousInitialGenerationProject(
  projectId: string,
  userId: string,
  storage?: StorageLike | null,
) {
  if (!projectId || !userId) {
    return false;
  }

  const targetStorage = getStorage(storage);
  const tasks = readTasksStorage(targetStorage);
  const anonymousOwnerId = getInitialGenerationNotificationOwnerId(projectId);
  const anonymousTask = tasks.users[anonymousOwnerId]?.projects[projectId];
  if (!anonymousTask) {
    return false;
  }

  const userTasks = tasks.users[userId] ?? { projects: {} };
  const currentTask = userTasks.projects[projectId];
  userTasks.projects[projectId] = !currentTask || anonymousTask.updatedAt >= currentTask.updatedAt
    ? anonymousTask
    : currentTask;
  tasks.users[userId] = userTasks;
  delete tasks.users[anonymousOwnerId];
  writeTasksStorage(targetStorage, tasks);
  return true;
}

export function clearInitialGenerationNotificationTasks(userId: string, storage?: StorageLike | null) {
  const targetStorage = getStorage(storage);
  const tasks = readTasksStorage(targetStorage);
  delete tasks.users[userId];
  writeTasksStorage(targetStorage, tasks);
}

export function readHandledInitialGenerationProjectIds(userId: string, storage?: StorageLike | null): string[] {
  return Object.entries(getUserTasks(userId, storage).projects)
    .filter(([, task]) => task.status === 'handled')
    .sort(([, left], [, right]) => right.updatedAt - left.updatedAt)
    .map(([projectId]) => projectId);
}

export function readArmedInitialGenerationProjectIds(userId: string, storage?: StorageLike | null): string[] {
  return Object.entries(getUserTasks(userId, storage).projects)
    .filter(([, task]) => task.status === 'armed')
    .sort(([, left], [, right]) => right.updatedAt - left.updatedAt)
    .map(([projectId]) => projectId);
}

export function armInitialGenerationProject(
  projectId: string,
  userId: string,
  storage?: StorageLike | null,
) {
  updateUserTasks(userId, current => {
    current.projects[projectId] = {
      status: 'armed',
      updatedAt: getNextTaskUpdatedAt(current),
    };
    return current;
  }, storage);
}

export function markInitialGenerationProjectHandled(
  projectId: string,
  userId: string,
  storage?: StorageLike | null,
) {
  updateUserTasks(userId, current => {
    current.projects[projectId] = {
      status: 'handled',
      updatedAt: getNextTaskUpdatedAt(current),
    };
    return current;
  }, storage);
}
